import React, { useCallback, useEffect, useRef, useState } from "react";
import { Icon } from "../shared/icons/IconLib";
import "./SortableCrudOptionList.css";

/** @typedef {{ id: string, value: string }} SurveyOptionRow */

export function newSurveyOptionRow(value = "") {
  return {
    id: `opt_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`,
    value,
  };
}

/**
 * @param {SurveyOptionRow[]} items
 * @param {number} fromIndex
 * @param {number} insertBeforeIndex — 0 … items.length
 */
export function reorderOptionRows(items, fromIndex, insertBeforeIndex) {
  const n = items.length;
  if (
    fromIndex < 0 ||
    fromIndex >= n ||
    insertBeforeIndex < 0 ||
    insertBeforeIndex > n
  ) {
    return items;
  }
  if (insertBeforeIndex === fromIndex || insertBeforeIndex === fromIndex + 1) {
    return items;
  }
  const next = [...items];
  const [removed] = next.splice(fromIndex, 1);
  let dest = insertBeforeIndex;
  if (fromIndex < insertBeforeIndex) {
    dest = insertBeforeIndex - 1;
  }
  next.splice(dest, 0, removed);
  return next;
}

function DragHandleGlyph() {
  return (
    <svg width="8" height="11" viewBox="0 0 14 18" aria-hidden>
      <circle cx="4" cy="4" r="1.4" fill="currentColor" />
      <circle cx="10" cy="4" r="1.4" fill="currentColor" />
      <circle cx="4" cy="9" r="1.4" fill="currentColor" />
      <circle cx="10" cy="9" r="1.4" fill="currentColor" />
      <circle cx="4" cy="14" r="1.4" fill="currentColor" />
      <circle cx="10" cy="14" r="1.4" fill="currentColor" />
    </svg>
  );
}

function PlusGlyph() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" aria-hidden>
      <path
        fill="currentColor"
        d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"
      />
    </svg>
  );
}

/**
 * Sortable, inline-editable list of string options (survey choices, etc.).
 *
 * @param {object} props
 * @param {SurveyOptionRow[]} props.items
 * @param {(rows: SurveyOptionRow[]) => void} props.onChange
 * @param {string} [props.addPlaceholder]
 * @param {string|null} [props.hint] — helper below list; pass null to omit
 * @param {string} [props.ariaLabel] — list label for accessibility
 */
function SortableCrudOptionList({
  items = [],
  onChange,
  addPlaceholder = "Add new option…",
  hint = null,
  ariaLabel = "Options",
}) {
  const [draft, setDraft] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editBuffer, setEditBuffer] = useState("");
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [dragFromIndex, setDragFromIndex] = useState(null);
  const [dropBeforeIndex, setDropBeforeIndex] = useState(null);
  const dragFromRef = useRef(null);
  /** Synced in dragOver; state alone is too stale for drop in React. */
  const dropBeforeRef = useRef(null);

  useEffect(() => {
    if (!confirmDeleteId) return;
    function onDocClick(e) {
      if (e.target.closest(".crud-option-delete-wrap")) return;
      setConfirmDeleteId(null);
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [confirmDeleteId]);

  const commitDraft = useCallback(() => {
    const t = (draft || "").trim();
    if (!t) return;
    onChange([...items, newSurveyOptionRow(t)]);
    setDraft("");
  }, [draft, items, onChange]);

  const updateRowValue = useCallback(
    (id, value) => {
      onChange(
        items.map((row) => (row.id === id ? { ...row, value } : row))
      );
    },
    [items, onChange]
  );

  const removeRow = useCallback(
    (id) => {
      onChange(items.filter((row) => row.id !== id));
      setConfirmDeleteId(null);
      if (editingId === id) {
        setEditingId(null);
        setEditBuffer("");
      }
    },
    [items, onChange, editingId]
  );

  const startEdit = useCallback((row) => {
    setEditingId(row.id);
    setEditBuffer(row.value);
  }, []);

  const commitEdit = useCallback(() => {
    if (!editingId) return;
    updateRowValue(editingId, editBuffer);
    setEditingId(null);
    setEditBuffer("");
  }, [editingId, editBuffer, updateRowValue]);

  const cancelEdit = useCallback(() => {
    setEditingId(null);
    setEditBuffer("");
  }, []);

  const handleRowDragStart = useCallback((e, index) => {
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", String(index));
    try {
      e.dataTransfer.setData("application/x-crud-option-index", String(index));
    } catch {
      /* IE / legacy */
    }
    dragFromRef.current = index;
    setDragFromIndex(index);
    dropBeforeRef.current = null;
    setDropBeforeIndex(null);
  }, []);

  const handleRowDragEnd = useCallback(() => {
    dragFromRef.current = null;
    dropBeforeRef.current = null;
    setDragFromIndex(null);
    setDropBeforeIndex(null);
  }, []);

  const handleRowDragOver = useCallback((e, rowIndex) => {
    if (dragFromRef.current == null) return;
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = "move";
    const rect = e.currentTarget.getBoundingClientRect();
    const mid = rect.top + rect.height / 2;
    const insertBefore = e.clientY <= mid ? rowIndex : rowIndex + 1;
    dropBeforeRef.current = insertBefore;
    setDropBeforeIndex(insertBefore);
  }, []);

  const handleListDragOver = useCallback((e) => {
    if (dragFromRef.current == null) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  }, []);

  const handleDragEnterEligible = useCallback((e) => {
    if (dragFromRef.current == null) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  }, []);

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      let from = dragFromRef.current;
      if (from == null || Number.isNaN(from)) {
        from = parseInt(e.dataTransfer.getData("text/plain"), 10);
      }
      if (from == null || Number.isNaN(from)) {
        try {
          from = parseInt(
            e.dataTransfer.getData("application/x-crud-option-index"),
            10
          );
        } catch {
          /* ignore */
        }
      }
      let insertBefore = dropBeforeRef.current;
      if (insertBefore == null || Number.isNaN(insertBefore)) {
        insertBefore = items.length;
      }
      if (Number.isNaN(from)) {
        handleRowDragEnd();
        return;
      }
      onChange(reorderOptionRows(items, from, insertBefore));
      handleRowDragEnd();
    },
    [items, onChange, handleRowDragEnd]
  );

  const handleRowDrop = useCallback(
    (e) => {
      e.stopPropagation();
      handleDrop(e);
    },
    [handleDrop]
  );

  const showLineBefore = (i) =>
    dragFromIndex != null && dropBeforeIndex === i;

  return (
    <div className="crud-option-list" aria-label={ariaLabel}>
      <div className="crud-option-list-add">
        <div className="crud-option-list-add-input-wrap">
          <span className="crud-option-list-add-icon" aria-hidden>
            <PlusGlyph />
          </span>
          <input
            type="text"
            className="crud-option-list-add-input"
            placeholder={addPlaceholder}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                commitDraft();
              }
            }}
            aria-label={addPlaceholder}
          />
        </div>
        <button
          type="button"
          className="crud-option-list-add-btn"
          onClick={commitDraft}
          disabled={!(draft || "").trim()}
        >
          Add
        </button>
      </div>

      <div
        className="crud-option-list-rows"
        onDragEnter={handleDragEnterEligible}
        onDragOver={handleListDragOver}
        onDrop={handleDrop}
      >
        {showLineBefore(0) && <div className="crud-option-drop-line" />}
        {items.map((row, index) => (
          <React.Fragment key={row.id}>
            <div
              className={
                "crud-option-row" +
                (dragFromIndex === index ? " crud-option-rowDragging" : "")
              }
              onDragEnter={handleDragEnterEligible}
              onDragOver={(ev) => handleRowDragOver(ev, index)}
              onDrop={handleRowDrop}
              title="Drag the handle to reorder"
            >
              <div
                className="crud-option-drag-handle"
                data-crud-drag-handle
                draggable
                onDragStart={(ev) => handleRowDragStart(ev, index)}
                onDragEnd={handleRowDragEnd}
                role="button"
                tabIndex={0}
                aria-label={`Drag to reorder option ${index + 1}`}
                onKeyDown={(ev) => {
                  if (ev.key === "Enter" || ev.key === " ") ev.preventDefault();
                }}
              >
                <DragHandleGlyph />
              </div>
              <span className="crud-option-index">{index + 1}</span>
              <div className="crud-option-value-wrap">
                {editingId === row.id ? (
                  <input
                    type="text"
                    className="crud-option-value-input"
                    value={editBuffer}
                    autoFocus
                    onChange={(e) => setEditBuffer(e.target.value)}
                    onBlur={commitEdit}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        commitEdit();
                      }
                      if (e.key === "Escape") {
                        e.preventDefault();
                        cancelEdit();
                      }
                    }}
                    aria-label={`Edit option ${index + 1}`}
                  />
                ) : (
                  <span
                    role="button"
                    tabIndex={0}
                    className="crud-option-value-text"
                    onClick={() => startEdit(row)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        startEdit(row);
                      }
                    }}
                  >
                    {row.value || (
                      <span style={{ color: "var(--hint-color)" }}>
                        Empty — click to edit
                      </span>
                    )}
                  </span>
                )}
              </div>
              <div className="crud-option-delete-wrap">
                <button
                  type="button"
                  className="crud-option-delete"
                  title="Remove option"
                  aria-label={`Remove option ${index + 1}`}
                  onClick={() =>
                    setConfirmDeleteId((id) =>
                      id === row.id ? null : row.id
                    )
                  }
                >
                  <Icon name="delete" size={14} />
                </button>
                {confirmDeleteId === row.id && (
                  <div className="crud-option-delete-popover" role="dialog" aria-label="Confirm delete">
                    <div className="crud-option-delete-popover-title">
                      Remove this option?
                    </div>
                    <div className="crud-option-delete-popover-actions">
                      <button
                        type="button"
                        onClick={() => removeRow(row.id)}
                      >
                        Remove
                      </button>
                      <button
                        type="button"
                        onClick={() => setConfirmDeleteId(null)}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
            {showLineBefore(index + 1) && (
              <div className="crud-option-drop-line" />
            )}
          </React.Fragment>
        ))}
      </div>
      {hint != null && hint !== "" && (
        <div className="crud-option-hint">{hint}</div>
      )}
    </div>
  );
}

export default SortableCrudOptionList;
